import { useToastNotificationContext } from '@sendible/design-system/src';
import { createContext, useContext, useMemo, Dispatch, SetStateAction, useState } from 'react';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import { composeTabs, MediaAttachedType, useBridgeContext } from '@sendible/shared-state-bridge';
import { supportedFileTypes, useUploadFiles } from '../../models/uploads';
import { MediaFileType } from '../../models/medias/types';

export type ComposeBoxContextType = {
  activeTabService: string;
  isComposeBoxOpen: boolean;
  isCanvaModalOpen: boolean;
  isFlickrModalOpen: boolean;
  isGiphyModalOpen: boolean;
  isMediaAttacherOpen: boolean;
  isPexelsModalOpen: boolean;
  isRecentlyUsedModalOpen: boolean;
  isURLModalOpen: boolean;
  isYoutubeModalOpen: boolean;
  activeComposeBoxTab: composeTabs;
  mediasForActiveTab?: MediaAttachedType;
  setIsCanvaModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsFlickrModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsGiphyModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsPexelsModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsRecentlyUsedModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsURLModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsYoutubeModalOpen: Dispatch<SetStateAction<boolean>>;
  validateAndUploadFiles: (files: File[]) => Promise<void>;
};

export const ComposeBoxContext = createContext({} as ComposeBoxContextType);

export const ComposeBoxProvider = ({ children }: Component) => {
  const [isCanvaModalOpen, setIsCanvaModalOpen] = useState(false);
  const [isFlickrModalOpen, setIsFlickrModalOpen] = useState(false);
  const [isGiphyModalOpen, setIsGiphyModalOpen] = useState(false);
  const [isPexelsModalOpen, setIsPexelsModalOpen] = useState(false);
  const [isRecentlyUsedModalOpen, setIsRecentlyUsedModalOpen] = useState(false);
  const [isURLModalOpen, setIsURLModalOpen] = useState(false);
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const { showToastNotification } = useToastNotificationContext();
  const [
    {
      compose: { tabsState, activeComposeBoxTab, isComposeBoxOpen, isMediaAttacherOpen },
    },
  ] = useBridgeContext();
  const uploadFiles = useUploadFiles({ uploadingFrom: 'compose-box', tabId: activeComposeBoxTab });

  const { t } = useTranslation('compose_box');

  const activeTabService = useMemo(() => {
    const activeTabsList = Object.keys(tabsState).filter((item) => item !== 'message');
    const filtered = activeComposeBoxTab === 'message' ? [] : activeTabsList.filter((item) => item === activeComposeBoxTab);

    return filtered.length === 1 ? filtered[0] : '';
  }, [tabsState, activeComposeBoxTab]);

  const mediasForActiveTab = tabsState[activeComposeBoxTab]?.mediaAttached;

  const validateAndUploadFiles = async (newFiles: File[]) => {
    const attachedTypes = Object.values(mediasForActiveTab || {}).map((media) => media.type);

    const newTypes = newFiles.map((file: File) => {
      return Object.entries(supportedFileTypes).find(([, prefix]) => file.type.startsWith(prefix))?.[0] as MediaFileType | undefined;
    });

    const totalTypes = [...attachedTypes, ...newTypes];

    const hasMixedTypes = !totalTypes.every((type) => type === totalTypes[0]);

    const imageCount = totalTypes.filter((type) => type === MediaFileType.Image).length;
    const nonImageCount = totalTypes.filter((type) => type !== MediaFileType.Image).length;

    const exceedsLimit = imageCount > 15 || nonImageCount > 1;

    if (hasMixedTypes || exceedsLimit) {
      showToastNotification({
        notificationText: t('mixedMediaError') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    } else {
      uploadFiles(Array.from(newFiles));
    }
  };

  return (
    <ComposeBoxContext.Provider
      value={{
        activeTabService,
        activeComposeBoxTab,
        isCanvaModalOpen,
        isComposeBoxOpen,
        isFlickrModalOpen,
        isGiphyModalOpen,
        isMediaAttacherOpen,
        isPexelsModalOpen,
        isRecentlyUsedModalOpen,
        isURLModalOpen,
        isYoutubeModalOpen,
        mediasForActiveTab,
        setIsCanvaModalOpen,
        setIsFlickrModalOpen,
        setIsGiphyModalOpen,
        setIsPexelsModalOpen,
        setIsRecentlyUsedModalOpen,
        setIsURLModalOpen,
        setIsYoutubeModalOpen,
        validateAndUploadFiles,
      }}
    >
      {children}
    </ComposeBoxContext.Provider>
  );
};

export const useComposeBoxContext = (): ComposeBoxContextType => useContext(ComposeBoxContext);
